import React, { useState, useEffect } from "react";
import {
  Box, Typography, Card, CardContent, Grid, Avatar, IconButton, Container,
  Tabs, Tab, CircularProgress, Chip, Paper, Button, ButtonGroup,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog,
  DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DescriptionIcon from "@mui/icons-material/Description";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import TableRowsIcon from "@mui/icons-material/TableRows";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import manImg from "../../Logos/GenderImages/man.png";
import womanImg from "../../Logos/GenderImages/girl.png";
import { fetchUserFromRedis } from "../../HomePage/AuthonticationScreens/Register";
import Footer from "../../Common/Footer";
import { apiRequest } from '../../../fetchSetup';


const CancelledOrdersScreen = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [cancelledBookings, setCancelledBookings] = useState([]);
  const [cancelledMedicineOrders, setCancelledMedicineOrders] = useState([]);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("table"); // "card" or "table"

  const CANCELLED_ORDERS_API = process.env.REACT_APP_CANCELLED_ORDERS_API;

  // Fetch user from Redis
  useEffect(() => {
    const fetchUser = async () => {
      const userId = sessionStorage.getItem("userId");
      const token = sessionStorage.getItem("token");
      if (!userId || !token) {
        setUser(null);
        setLoading(false);
        return;
      }
      try {
        const userData = await fetchUserFromRedis(userId, token);
        setUser(userData || null);
      } catch (err) {
        console.error("Error fetching user from Redis:", err);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  // Fetch cancelled orders
  useEffect(() => {
    const fetchCancelledOrders = async () => {
      if (!user?.user_id && !user?.email) {
        setLoading(false);
        return;
      }

      try {
        const queryParam = user.user_id
          ? `user_id=${encodeURIComponent(user.user_id)}`
          : `email=${encodeURIComponent(user.email)}`;

        const response = await apiRequest(`${CANCELLED_ORDERS_API}?${queryParam}`);
        const data = await response.json();

        if (response.ok) {
          setCancelledBookings(data.cancelled_bookings || []);
          setCancelledMedicineOrders(data.cancelled_medicine_orders || []);
          setError(null);
        } else {
          setError(data.error || "Failed to fetch orders");
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCancelledOrders();
  }, [user, CANCELLED_ORDERS_API]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getDisplayOrders = () => {
    if (activeTab === 1) return cancelledBookings;
    if (activeTab === 2) return cancelledMedicineOrders;
    return [...cancelledBookings, ...cancelledMedicineOrders].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
  };

  const displayOrders = getDisplayOrders();

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "80vh" }}>
        <CircularProgress sx={{ color: "#006D77" }} />
      </Box>
    );
  }

  return (
    <>

      <Paper elevation={2} sx={{ bgcolor: "#fff", mb: 3 }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              py: { xs: 1.5, sm: 2, md: 3 },
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <IconButton
              onClick={() => window.history.back()}
              sx={{
                flexShrink: 0,
                backgroundColor: "white",
                boxShadow: 1,
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
            >
              <ArrowBackIcon />
            </IconButton>

            <Typography
              variant="h4"
              sx={{
                color: "#006D77",
                fontWeight: 600,
                fontSize: { xs: "1rem", sm: "1.4rem", md: "2rem" },
                textAlign: "center",
                flex: 1,
              }}
            >
              Cancelled Orders
            </Typography>

            {/* Spacer to keep title centered */}
            <Box sx={{ width: 40, flexShrink: 0 }} />
          </Box>
        </Container>
      </Paper>


      <Container maxWidth="lg" sx={{ py: 4, mb: 10 }}>
        {error && (
          <Box sx={{ mb: 3, p: 2, backgroundColor: "#FEE2E2", borderRadius: 2 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: { xs: 1.5, sm: 0 },
              mb: 3,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                borderBottom: 1,
                borderColor: "#006D77",
                maxWidth: { xs: "100%", sm: "auto" },
                "& .MuiTab-root": {
                  color: "#006D77",
                  fontWeight: 600,
                  fontSize: { xs: "0.7rem", sm: "0.875rem" },
                  minWidth: { xs: "auto", sm: 120 },
                  px: { xs: 1, sm: 2 },
                },
                "& .Mui-selected": { color: "#006D77 !important" },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#006D77",
                  height: 3,
                },
              }}
            >
              <Tab label={`All (${cancelledBookings.length + cancelledMedicineOrders.length})`} />
              <Tab label={`Service Bookings (${cancelledBookings.length})`} />
              <Tab label={`Medicine Orders (${cancelledMedicineOrders.length})`} />
            </Tabs>

            {/* View Toggle */}
            <ButtonGroup variant="outlined" size="small" sx={{ flexShrink: 0 }}>
              <Button
                onClick={() => setViewMode("card")}
                variant={viewMode === "card" ? "contained" : "outlined"}
                startIcon={<ViewModuleIcon />}
                sx={{
                  bgcolor: viewMode === "card" ? "#006D77" : "transparent",
                  color: viewMode === "card" ? "white" : "#006D77",
                  borderColor: "#006D77",
                  fontSize: { xs: "0.7rem", sm: "0.875rem" },
                  px: { xs: 1, sm: 2 },
                  "&:hover": {
                    bgcolor: viewMode === "card" ? "#004F55" : "#E0F7FA",
                    borderColor: "#006D77",
                  },
                }}
              >
                {/* Hide label on mobile */}
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  Card View
                </Box>
              </Button>
              <Button
                onClick={() => setViewMode("table")}
                variant={viewMode === "table" ? "contained" : "outlined"}
                startIcon={<TableRowsIcon />}
                sx={{
                  bgcolor: viewMode === "table" ? "#006D77" : "transparent",
                  color: viewMode === "table" ? "white" : "#006D77",
                  borderColor: "#006D77",
                  fontSize: { xs: "0.7rem", sm: "0.875rem" },
                  px: { xs: 1, sm: 2 },
                  "&:hover": {
                    bgcolor: viewMode === "table" ? "#004F55" : "#E0F7FA",
                    borderColor: "#006D77",
                  },
                }}
              >
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  Table View
                </Box>
              </Button>
            </ButtonGroup>
          </Box>

        {/* Content */}
        {displayOrders.length === 0 ? (
          <Box textAlign="center" sx={{ py: 8 }}>
            <Typography sx={{ fontSize: 50, mb: 2 }}>❌</Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#111827", mb: 1 }}>
              No Cancelled Orders
            </Typography>
            <Typography sx={{ color: "#6B7280", fontSize: 14 }}>
              You currently have no cancelled orders.
            </Typography>
          </Box>
        ) : viewMode === "card" ? (
          <Grid container spacing={3}>
            {displayOrders.map((order) => (
              <Grid item xs={12} key={order.id}>
                {order.type === "booking" ? (
                  <BookingCard booking={order} formatDate={formatDate} />
                ) : (
                  <MedicineOrderCard order={order} formatDate={formatDate} />
                )}
              </Grid>
            ))}
          </Grid>
        ) : (
          <CancelledOrdersTable orders={displayOrders} formatDate={formatDate} />
        )}
      </Container>
      <Footer />
    </>
  );
};

// Service Booking Card
const BookingCard = ({ booking, formatDate }) => {
  return (
    <Card
  sx={{
    width: 350,
    maxWidth: 350,
    backgroundColor: "#FFF",
    borderRadius: 3,
    boxShadow: 3,
    border: "2px solid #D4F8D4",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  }}
>
      <Box
        sx={{
          p: 2,
          backgroundColor: "#D4F8D4",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LocalHospitalIcon sx={{ color: "#006D77" }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#006D77" }}>
            Service Booking
          </Typography>
        </Box>
        <Chip
          label="CANCELLED"
          size="small"
          sx={{ backgroundColor: "#006D77", color: "white", fontWeight: 600, pointerEvents: "none" }}
        />
      </Box>

      <CardContent sx={{ flexGrow: 1 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Avatar sx={{ bgcolor: "transparent", width: 64, height: 64 }}>
                <img
                  src={booking.gender === "Male" || booking.gender === "male" ? manImg : womanImg}
                  alt={booking.gender}
                  style={{ width: "100%", height: "100%" }}
                />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {booking.service_type}
                </Typography>
                <Typography variant="caption" sx={{ color: "#666" }}>
                  ID: {booking.id}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <PersonIcon sx={{ fontSize: 20, color: "#666" }} />
              <Typography>
                <strong>Patient:</strong> {booking.guest_name}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <CalendarTodayIcon sx={{ fontSize: 20, color: "#666" }} />
              <Typography>
                <strong>Date:</strong> {formatDate(booking.date)}
              </Typography>
            </Box>

            <Typography>
              <strong>Age:</strong> {booking.age} years • <strong>Gender:</strong> {booking.gender}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ backgroundColor: "#F9FAFB", p: 2, mb: 2, boxShadow: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "#006D77" }}>
                Payment Details
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="body2">Method:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {booking.payment?.method || "N/A"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Amount:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  ₹{booking.payment?.amount || 0}
                </Typography>
              </Box>
            </Card>

            <Card sx={{ backgroundColor: "#F9FAFB", p: 2, border: "1px solid #D4F8D4", boxShadow: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#006D77", mb: 1 }}>
                Cancellation Details
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Tests:</strong> {booking.diagnostic_tests}
              </Typography>
              <Typography variant="body2">
                <strong>Reason:</strong> {booking.cancellation_reason}
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// Medicine Order Card Component
const MedicineOrderCard = ({ order, formatDate }) => {
const isPrescriptionOrder = order.order_type === "prescription";
const [prescriptionFiles, setPrescriptionFiles] = useState([]);
const [loadingPrescription, setLoadingPrescription] = useState(false);
const [selectedFile, setSelectedFile] = useState(null);
const [fileDialogOpen, setFileDialogOpen] = useState(false);
const [fileType, setFileType] = useState('');

// Fetch prescription files when component mounts for prescription orders
useEffect(() => {
  const fetchPrescriptionFiles = async () => {
    if (!isPrescriptionOrder || !order.id) return;
    
    setLoadingPrescription(true);
    try {
      const apiUrl = `${process.env.REACT_APP_PRESCRITON_IMAGE_API_URL}/${order.id}`;
      const response = await apiRequest(apiUrl);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.files && data.files.length > 0) {
          setPrescriptionFiles(data.files);
        } else {
          setPrescriptionFiles([]);
        }
      } else {
        setPrescriptionFiles([]);
      }
    } catch (error) {
      console.error("Error fetching prescription files:", error);
    } finally {
      setLoadingPrescription(false);
    }
  };

  fetchPrescriptionFiles();
}, [isPrescriptionOrder, order.id]);

// Function to get file URL
const getFileUrl = (file) => {
  if (!file) return '';
  
  if (file.url && file.url.startsWith('http')) {
    return file.url;
  }
  
  if (file.cloudfront_url) {
    return file.cloudfront_url;
  }
  
  if (file.download_url) {
    return file.download_url;
  }
  
  return '';
};

// Get file type from URL
const getFileTypeFromUrl = (url) => {
  if (!url) return 'unknown';
  const extension = url.split('.').pop().toLowerCase().split('?')[0];
  
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
    return 'image';
  } else if (extension === 'pdf') {
    return 'pdf';
  } else if (['doc', 'docx'].includes(extension)) {
    return 'document';
  }
  return 'unknown';
};

// Open file viewer
const openFileViewer = (file) => {
  const fileUrl = getFileUrl(file);
  if (fileUrl) {
    const type = getFileTypeFromUrl(fileUrl);
    setFileType(type);
    setSelectedFile(fileUrl);
    setFileDialogOpen(true);
  }
};

// File Viewer Dialog Component
const FileViewerDialog = () => {
  return (
    <Dialog
      open={fileDialogOpen}
      onClose={() => setFileDialogOpen(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{ 
        sx: { 
          borderRadius: 3,
          maxHeight: '90vh'
        } 
      }}
    >
      <DialogTitle sx={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        backgroundColor: "#006D77",
        color: "white",
        py: 1.5
      }}>
        <Typography variant="h6">Prescription Viewer</Typography>
        <IconButton 
          onClick={() => setFileDialogOpen(false)} 
          size="small"
          sx={{ color: "white" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, bgcolor: "#f8fafc" }}>
        {fileType === 'image' && (
          <Box sx={{ 
            display: "flex", 
            justifyContent: "center", 
            alignItems: "center",
            minHeight: "500px",
            p: 2
          }}>
            <img
              src={selectedFile}
              alt="Prescription"
              style={{ 
                maxWidth: "100%", 
                maxHeight: "75vh", 
                objectFit: "contain",
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
              }}
            />
          </Box>
        )}
        
        {fileType === 'pdf' && (
          <Box sx={{ 
            display: "flex", 
            flexDirection: "column",
            height: "75vh",
            bgcolor: "white"
          }}>
            <iframe
              src={selectedFile}
              style={{ 
                width: "100%", 
                height: "100%", 
                border: "none"
              }}
              title="PDF Viewer"
            />
          </Box>
        )}
        
        {(fileType === 'document' || fileType === 'unknown') && (
          <Box sx={{ 
            textAlign: "center", 
            p: 6,
            minHeight: "400px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center"
          }}>
            <Typography sx={{ fontSize: 80, color: "#64748b", mb: 2 }}>
              📝
            </Typography>
            <Typography sx={{ fontSize: 18, color: "#1e293b", mb: 2, fontWeight: 600 }}>
              Document Preview Not Available
            </Typography>
            <Typography sx={{ fontSize: 14, color: "#64748b", mb: 3 }}>
              This file format cannot be previewed in the browser
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 2, 
        backgroundColor: "#f8fafc",
        justifyContent: "space-between"
      }}>
      </DialogActions>
    </Dialog>
  );
};

// Get icon based on file type
const getFileIcon = (type) => {
  if (type === 'image') return '🖼️';
  if (type === 'pdf') return '📄';
  return '📝';
};

return (
  <>
    <Card
      sx={{
        width: 350,
        maxWidth: 350,
        backgroundColor: "#FFF",
        borderRadius: 3,
        boxShadow: 3,
        border: "2px solid #D4F8D4",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          p: 2,
          backgroundColor: "#D4F8D4",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LocalPharmacyIcon sx={{ color: "#006D77" }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#006D77" }}>
            Medicine Order • {isPrescriptionOrder ? "Prescription" : "Pharmacy"}
          </Typography>
        </Box>
        <Chip
          label="CANCELLED"
          size="small"
          sx={{ backgroundColor: "#006D77", color: "white", fontWeight: 600, pointerEvents: "none" }}
        />
      </Box>

      <CardContent sx={{ flexGrow: 1 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Order Details
            </Typography>
            <Typography variant="caption" sx={{ color: "#666", mb: 2, display: "block" }}>
              Order ID: {order.id}
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Items Ordered:
            </Typography>
            <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
              {order.items?.length > 0 ? (
              order.items.map((item, idx) => (
                <Card key={idx} sx={{ backgroundColor: "#F9FAFB", p: 2, mb: 1, boxShadow: 1 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.medicine_name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        Qty: {item.quantity}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      ₹{item.total_price}
                    </Typography>
                  </Box>
                </Card>
                ))
              ) : isPrescriptionOrder ? (
                <Card sx={{ backgroundColor: "#FFF3CD", p: 2, mb: 1, boxShadow: 1, border: "1px solid #FFE082" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#B45309" }}>
                    📋 Prescription-based Order
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#666", display: "block", mt: 0.5, fontStyle: "italic" }}>
                    {order.notes || "Order placed with uploaded prescription"}
                  </Typography>
                </Card>
              ) : (
                <Typography variant="body2" sx={{ color: "#666", fontStyle: "italic" }}>
                  No items listed
                </Typography>
              )}
            </Box>

            {/* Prescription Section */}
            {isPrescriptionOrder && (
              <Card sx={{ 
                backgroundColor: "#FFF3CD", 
                p: 2, 
                mt: 2, 
                boxShadow: 1,
                border: "1px solid #FFE082"
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <DescriptionIcon sx={{ color: "#D97706" }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#D97706" }}>
                    Prescription Files
                  </Typography>
                </Box>
                
                {loadingPrescription ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center", py: 2 }}>
                    <CircularProgress size={16} sx={{ color: "#D97706" }} />
                    <Typography variant="caption" sx={{ color: "#666" }}>
                      Loading prescription files...
                    </Typography>
                  </Box>
                ) : prescriptionFiles.length > 0 ? (
                  <Box>
                    <Typography variant="caption" sx={{ color: "#666", display: "block", mb: 1 }}>
                      {prescriptionFiles.length} file{prescriptionFiles.length > 1 ? 's' : ''} available for viewing
                    </Typography>
                    <Grid container spacing={1}>
                      {prescriptionFiles.map((file, index) => {
                        const fileUrl = getFileUrl(file);
                        const type = getFileTypeFromUrl(fileUrl);
                        return (
                          <Grid item xs={12} key={index}>
                            <Card
                              elevation={1}
                              sx={{
                                cursor: "pointer",
                                transition: "all 0.2s",
                                "&:hover": { 
                                  transform: "translateY(-2px)", 
                                  boxShadow: 3,
                                  backgroundColor: "#FFF8E1"
                                },
                                p: 1.5,
                                mb: 1,
                              }}
                              onClick={() => openFileViewer(file)}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <Typography sx={{ fontSize: 24 }}>
                                  {getFileIcon(type)}
                                </Typography>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" sx={{ color: "#1e293b", display: "block", fontWeight: 500 }}>
                                    {file.filename || `Prescription ${index + 1}`}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "#666", display: "block", fontSize: "0.7rem" }}>
                                    Click to view • {type.toUpperCase()}
                                  </Typography>
                                </Box>
                                <Typography sx={{ color: "#D97706", fontSize: 12 }}>
                                  VIEW
                                </Typography>
                              </Box>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                ) : order.prescription_url ? (
                  <Box>
                    <Button
                      variant="outlined"
                      startIcon={<DescriptionIcon />}
                      onClick={() => {
                        const type = getFileTypeFromUrl(order.prescription_url);
                        setFileType(type);
                        setSelectedFile(order.prescription_url);
                        setFileDialogOpen(true);
                      }}
                      sx={{
                        borderColor: "#D97706",
                        color: "#D97706",
                        textTransform: "none",
                        "&:hover": {
                          borderColor: "#B45309",
                          backgroundColor: "rgba(212, 212, 216, 0.1)"
                        }
                      }}
                    >
                      View Prescription
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body2" sx={{ color: "#B45309", mb: 0.5, fontWeight: 500 }}>
                      📄 Prescription was uploaded
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#666", display: "block" }}>
                      {order.notes || "Prescription files were uploaded with this order"}
                    </Typography>
                  </Box>
                )}
              </Card>
            )}
            
            {/* For pharmacy orders with prescription URL */}
            {!isPrescriptionOrder && order.prescription_url && (
              <Card sx={{ backgroundColor: "#EBF8FF", p: 2, mt: 2, boxShadow: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <DescriptionIcon sx={{ color: "#2B6CB0" }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#2B6CB0" }}>
                    Prescription
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<DescriptionIcon />}
                  onClick={() => {
                    const type = getFileTypeFromUrl(order.prescription_url);
                    setFileType(type);
                    setSelectedFile(order.prescription_url);
                    setFileDialogOpen(true);
                  }}
                  sx={{
                    borderColor: "#2B6CB0",
                    color: "#2B6CB0",
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#1E40AF",
                      backgroundColor: "rgba(219, 234, 254, 0.3)"
                    }
                  }}
                >
                  View Prescription
                </Button>
              </Card>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            {order.delivery_address && (
              <Card sx={{ backgroundColor: "#F9FAFB", p: 2, mb: 2, boxShadow: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <LocationOnIcon sx={{ fontSize: 20, color: "#666" }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Delivery Address
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Name: {order.delivery_address.full_name}
                </Typography>
                <Typography variant="body2">Phone: {order.delivery_address.phone}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Community: {order.delivery_address.community_name}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Block: {order.delivery_address.address_line1}
                  {order.delivery_address.address_line2 && `, ${order.delivery_address.address_line2}`}
                </Typography>
                <Typography variant="body2">
                  {order.delivery_address.city}, {order.delivery_address.state} - {order.delivery_address.pincode}
                </Typography>
              </Card>
            )}

            <Card sx={{ backgroundColor: "#F9FAFB", p: 2, mb: 2, boxShadow: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "#006D77" }}>
                Payment Summary
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="body2">Subtotal:</Typography>
                <Typography variant="body2">₹{order.subtotal}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="body2">Delivery:</Typography>
                <Typography variant="body2">
                  {order.delivery_charge === 0 ? "FREE" : `₹${order.delivery_charge}`}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  borderTop: 1,
                  borderColor: "divider",
                  pt: 1,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Total:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  ₹{order.total_amount}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: "#666", mt: 1, display: "block" }}>
                Payment: {order.payment?.method} {order.payment?.status === "refunded" && " • Refunded"}
              </Typography>
            </Card>

            <Card sx={{ backgroundColor: "#F9FAFB", p: 2, border: "1px solid #D4F8D4", boxShadow: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#006D77", mb: 1 }}>
                Cancellation Details
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Cancelled on:</strong> {formatDate(order.cancellation_info?.cancelled_at || order.created_at)}
              </Typography>
              {order.cancellation_info?.reason && (
                <Typography variant="body2">
                  <strong>Reason:</strong> {order.cancellation_info.reason}
                </Typography>
              )}
            </Card>
          </Grid>
        </Grid>
      </CardContent>
    </Card>

    <FileViewerDialog />
  </>
  );
};

// Cancelled Orders Table Component
const CancelledOrdersTable = ({ orders, formatDate }) => {
  return (
    <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: "#006D77" }}>
            <TableCell sx={{ color: "white", fontWeight: 700 }}>Type</TableCell>
            <TableCell sx={{ color: "white", fontWeight: 700 }}>ID</TableCell>
            <TableCell sx={{ color: "white", fontWeight: 700 }}>Patient/Name</TableCell>
            <TableCell sx={{ color: "white", fontWeight: 700 }}>Service/Order Type</TableCell>
            <TableCell sx={{ color: "white", fontWeight: 700 }}>Date</TableCell>
            <TableCell sx={{ color: "white", fontWeight: 700 }}>Amount</TableCell>
            <TableCell sx={{ color: "white", fontWeight: 700 }}>Payment Method</TableCell>
            <TableCell sx={{ color: "white", fontWeight: 700 }}>Cancellation Reason</TableCell>
            <TableCell sx={{ color: "white", fontWeight: 700 }}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((order, index) => (
            <TableRow
              key={order.id || index}
              sx={{
                "&:hover": { bgcolor: "#F0F9FF" },
                "&:nth-of-type(odd)": { bgcolor: "#FAFAFA" },
              }}
            >
              <TableCell>
                {order.type === "booking" ? (
                  <Chip
                    icon={<LocalHospitalIcon />}
                    label="Booking"
                    size="small"
                    sx={{
                      bgcolor: "#D4F8D4",
                      color: "#006D77",
                      fontWeight: 600,
                    }}
                  />
                ) : (
                  <Chip
                    icon={<LocalPharmacyIcon />}
                    label="Medicine"
                    size="small"
                    sx={{
                      bgcolor: "#E0F7FA",
                      color: "#006D77",
                      fontWeight: 600,
                    }}
                  />
                )}
              </TableCell>
              <TableCell sx={{ fontSize: "0.875rem" }}>{order.id}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                {order.type === "booking" ? order.guest_name : order.delivery_address?.full_name || "-"}
              </TableCell>
              <TableCell>
                {order.type === "booking"
                  ? order.service_type
                  : order.order_type === "prescription"
                  ? "Prescription"
                  : "Pharmacy"}
              </TableCell>
              <TableCell sx={{ fontSize: "0.875rem" }}>{formatDate(order.date || order.created_at)}</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#006D77" }}>
                ₹{order.payment?.amount || order.total_amount || 0}
              </TableCell>
              <TableCell>{order.payment?.method || "N/A"}</TableCell>
              <TableCell sx={{ maxWidth: 200 }}>
                <Typography variant="body2" sx={{ fontSize: "0.875rem" }} noWrap>
                  {order.type === "booking"
                    ? order.cancellation_reason
                    : order.cancellation_info?.reason || "N/A"}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label="CANCELLED"
                  size="small"
                  sx={{
                    backgroundColor: "#006D77",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                  }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CancelledOrdersScreen;